import { useRef } from 'react';
import { toast } from 'sonner';
import {
  CopyIcon,
  MailIcon,
  MessageSquareIcon,
  Share2Icon,
  XIcon,
} from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { copyText } from '@/lib/utils';
import type { InvoiceShareLinkDto } from '@/lib/api/invoices';

import { formatDateTime } from './helpers';

interface Props {
  link: InvoiceShareLinkDto;
  /** Job customer's contact, when resolvable — prefilled as the mail/SMS recipient. */
  recipientEmail?: string | null;
  recipientPhone?: string | null;
  onClose: () => void;
}

const canNativeShare = () =>
  typeof navigator !== 'undefined' && typeof navigator.share === 'function';

// Shown after `POST .../share-link` succeeds. Mirrors `routes/quotes/share-link-panel.tsx`.
export function ShareLinkPanel({
  link,
  recipientEmail,
  recipientPhone,
  onClose,
}: Props) {
  const { t } = useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);

  const body = `${t.invoiceShareEmailBody}\n\n${link.url}`;
  const mailtoHref = `mailto:${recipientEmail ?? ''}?subject=${encodeURIComponent(
    t.invoiceShareEmailSubject,
  )}&body=${encodeURIComponent(body)}`;
  const smsHref = `sms:${recipientPhone ?? ''}?&body=${encodeURIComponent(body)}`;

  async function handleCopy() {
    const ok = await copyText(link.url);
    if (ok) {
      toast.success(t.invoiceLinkCopied);
    } else {
      inputRef.current?.select();
      toast.error(t.invoiceLinkCopyFailed);
    }
  }

  async function handleNativeShare() {
    try {
      await navigator.share({
        title: t.invoiceShareEmailSubject,
        text: t.invoiceShareEmailBody,
        url: link.url,
      });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const ok = await copyText(link.url);
      if (ok) toast.success(t.invoiceLinkCopied);
    }
  }

  return (
    <div
      className="flex flex-col gap-3 border-t pt-4"
      data-testid="invoice-share-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{t.invoiceShareLinkPanelTitle}</p>
          <p className="text-xs text-muted-foreground">{t.invoiceShareLinkHint}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t.invoiceShareClose}
          className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
          data-testid="invoice-share-panel-close"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          readOnly
          value={link.url}
          onFocus={(e) => e.currentTarget.select()}
          data-testid="invoice-share-url"
          className="flex-1 min-w-0 px-3 py-2 text-sm border rounded-md bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <Button
          type="button"
          size="sm"
          onClick={handleCopy}
          data-testid="invoice-share-copy"
        >
          <CopyIcon className="w-3.5 h-3.5" />
          {t.invoiceShareCopy}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {canNativeShare() && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleNativeShare}
            data-testid="invoice-share-native"
          >
            <Share2Icon className="w-3.5 h-3.5" />
            {t.invoiceShareNative}
          </Button>
        )}
        <Button asChild size="sm" variant="outline" data-testid="invoice-share-email">
          <a href={mailtoHref}>
            <MailIcon className="w-3.5 h-3.5" />
            {t.invoiceShareEmail}
          </a>
        </Button>
        <Button asChild size="sm" variant="outline" data-testid="invoice-share-text">
          <a href={smsHref}>
            <MessageSquareIcon className="w-3.5 h-3.5" />
            {t.invoiceShareText}
          </a>
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        {t.invoiceShareExpires} {formatDateTime(link.expires_at)}
      </p>
    </div>
  );
}
